/**
 * Map a tax-return extract envelope onto portal DerivedDetermination rows.
 * Wave 1 — form / tax_year / basis only. Never invents Confirm-ready quotes
 * from the candidate; verbatim comes from the extract proposal or the
 * unavailable marker.
 */
import { locationHintFor } from "./opening_location_hints.js";
import type {
  DerivedDetermination,
  OpeningDocumentExtractResponse,
  OpeningDocumentExtractStatus,
} from "./types.js";

/** Matches `pipeline.opening.return_extract.VERBATIM_SOURCE_UNAVAILABLE`. */
const SOURCE_UNAVAILABLE = "no source text available for this reading";

const TAX_FIELDS = ["form", "tax_year", "basis"] as const;

/**
 * Owner 2026-08-14: when OCR dispatch cannot run, the crop control is
 * hidden and this is the standing copy. Manual read is the normal path
 * for a scan — not an outage, not temporary, not an apology.
 *
 * Driven from GET `failureReason: skipped_no_secret` (live Worker
 * capability). When secrets are set, GET stops emitting that reason and
 * the crop path returns on its own.
 */
export const OCR_SCAN_MANUAL_COPY =
  "This document is a scan, so it can't be read automatically. Read the value from the document and type it below, exactly as it appears.";

/** Confirm-refused on an unusable tax field when the crop is hidden. */
export const OCR_SCAN_MANUAL_CONFIRM_REFUSED_COPY =
  "Confirm is refused — no cited span. Type the reading you confirm, or choose No source found (does not unlock Submit).";

/** verify-quote `{via:"none"}` when the crop is hidden. */
export const OCR_SCAN_MANUAL_QUOTE_NONE_COPY =
  "This document has no searchable text (scanned or unextracted). Type the value exactly as it appears, or choose No source found if you looked and cannot quote (does not unlock Submit).";

export function extractStatusCopy(
  status: OpeningDocumentExtractStatus | undefined,
): string | null {
  if (status === "pending") {
    return "No extract result is available yet. You can wait, or look at the usual location below.";
  }
  if (status === "failed") {
    return "We could not read this document automatically. Read the value from the document and type it below, exactly as it appears.";
  }
  if (status === "not_extractable") {
    return "This document can't be read automatically yet. Enter the figures from it by hand.";
  }
  return null;
}

export const EXTRACT_STATUS_LOAD_ERROR_COPY =
  "Could not load extract status. Look at the usual location below.";

export function ocrPollShouldContinue(view: {
  status: "pending" | "ready" | "failed";
  failureReason: string | null;
}): "stop" | "continue" {
  if (view.status === "pending" && view.failureReason === null) {
    return "continue";
  }
  return "stop";
}

/** Crop affordance is offered only when dispatch can run right now. */
export function ocrCropOffered(view: {
  failureReason: string | null;
}): boolean {
  return view.failureReason !== "skipped_no_secret";
}

export const OCR_POLL_INTERVAL_MS = 1500;
export const OCR_POLL_MAX_TICKS = 40;

export function ocrStatusCopy(view: {
  status: "pending" | "ready" | "failed";
  failureReason: string | null;
}): string {
  if (view.status === "ready") {
    return "OCR finished — confirm the reading before Correct. Nothing was auto-applied.";
  }
  if (view.status === "pending") {
    return "OCR is still running on the crop you provided.";
  }
  const reason = view.failureReason;
  if (reason === "skipped_no_secret") {
    return OCR_SCAN_MANUAL_COPY;
  }
  if (reason === "no_crop") {
    return "Paste a screenshot crop of the usual location to read this field.";
  }
  if (reason === "not_dispatched") {
    return "OCR has not started on this crop. Paste a screenshot crop again to retry, or type the value from the document below.";
  }
  if (reason === "dispatch_failed") {
    return "OCR could not be started. Paste a screenshot crop again, or type the value from the document below.";
  }
  return "OCR could not read this crop. Try a tighter screenshot of the usual location, or choose No source found.";
}

function stringField(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

function citationFromProposal(raw: unknown): { page: number; line: string } | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const rec = raw as { page?: unknown; line_label?: unknown };
  const page = rec.page;
  const line = rec.line_label;
  if (typeof page !== "number" || page < 1) return undefined;
  if (typeof line !== "string" || line.trim().length === 0) return undefined;
  return { page, line };
}

function optionalLocationHint(
  field: string,
  form?: string | null,
): { locationHint: string } | Record<string, never> {
  const hint = locationHintFor(field, form);
  return hint !== null ? { locationHint: hint } : {};
}

function optionalCitation(
  raw: unknown,
): { citation: { page: number; line: string } } | Record<string, never> {
  const citation = citationFromProposal(raw);
  return citation !== undefined ? { citation } : {};
}

export function determinationsFromExtract(
  view: OpeningDocumentExtractResponse,
): DerivedDetermination[] {
  const formFromExtract = stringField(view.extract?.["form"]);
  const byField = new Map<string, DerivedDetermination>();

  if (view.status === "ready" && view.extract !== null) {
    const proposals = view.extract["proposals"];
    if (Array.isArray(proposals)) {
      for (const raw of proposals) {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw)) continue;
        const rec = raw as {
          field?: unknown;
          candidate_value?: unknown;
          verbatim_source_text?: unknown;
          page?: unknown;
          line_label?: unknown;
        };
        const field = stringField(rec.field);
        if (field === null || !(TAX_FIELDS as readonly string[]).includes(field)) {
          continue;
        }
        const candidate = stringField(rec.candidate_value) ?? "unextracted";
        const sourceText =
          stringField(rec.verbatim_source_text) ?? SOURCE_UNAVAILABLE;
        byField.set(field, {
          field,
          candidate,
          sourceText,
          extractStatus: "ready",
          ...optionalCitation(rec),
          ...optionalLocationHint(field, formFromExtract),
        });
      }
    }
  }

  const out: DerivedDetermination[] = [];
  for (const field of TAX_FIELDS) {
    const existing = byField.get(field);
    if (existing !== undefined) {
      out.push(existing);
      continue;
    }
    out.push({
      field,
      candidate: "unextracted — open the return and state the reading",
      sourceText: SOURCE_UNAVAILABLE,
      extractStatus: view.status === "ready" ? "ready" : view.status,
      ...optionalLocationHint(field, formFromExtract),
    });
  }
  return out;
}
